/**
 * The Flow builder — the concept's `FlowEditor.jsx`, ported to TypeScript and
 * Tailwind and wired to the real API. Slice 69.
 *
 * ## WHAT IT IS, TOP TO BOTTOM
 *
 * The reference's frame, kept: a top bar reading "Close editor · Editing ·
 * Saved 12s ago · name ▾ · Version 3 · Edit/Test/Run · Launch"; a dotted
 * canvas you drag to pan, with zoom and fit; the Start card, round gradient
 * logic nodes with their label under them, green WhatsApp bubbles whose
 * quick-reply buttons are each their own port; a "+" on every free port that
 * opens the add menu with **Suggest next step** first; suggested steps drawn
 * dashed purple with ✓ / ✕; a side drawer per step; the pre-flight sheet; the
 * test panel; run mode with live counts and edges as thick as their traffic.
 *
 * ## WHAT IS REAL NOW
 *
 * Everything the concept simulated is an API call here, and nothing in this
 * file invents a figure:
 *
 *   - **Autosave** — every change, debounced ~0.9 s, is `PUT /flows/:id/draft`.
 *     The draft is parsed against `saveFlowDraftRequestSchema` first; a draft
 *     the API would refuse is not sent, and the top bar says what is wrong and
 *     on which step instead of "Saved". Editing a live flow with no draft
 *     creates one ("Editing Version 3 · Version 2 is live"); people already
 *     inside keep walking the version they entered.
 *   - **Suggest, pre-flight, test** read the SAVED draft, so the pending save
 *     is flushed before each (see `lib/api/flows.ts`).
 *   - **Launch / Publish** is disabled while pre-flight blocks; the API
 *     enforces the same checks on `POST /launch`.
 *   - **Run** reads `GET /stats` for the live version every 30 s.
 *
 * ## WHY ITS STATE IS ONE GRAPH, NOT TWO
 *
 * The concept kept nodes and edges in two `useState`s and occasionally had to
 * reason about one changing without the other. A step whose ports change (a
 * template with other buttons, a split row removed) must change its edges in
 * the same update, or autosave can catch the half-way state and the API
 * refuses an edge on a port that no longer exists. So the graph is one value,
 * and every edit is a pure `FlowGraph → FlowGraph`.
 *
 * ## ACCESSIBILITY
 *
 * Every step is a button (named "Wait: For 1 day. Configure"), every free
 * port a button ("Add a step after “Not now”"), the add menu a real ARIA menu,
 * the drawer and the sheet return focus where they came from, the canvas
 * viewport is focusable and scrolls with the arrow keys, counts that change
 * are `aria-live="polite"`, and motion is `motion-safe` only. Below 640px the
 * canvas is replaced by `FlowList`, the same graph as a list.
 */
import {
  FLOW_STATUS_LABEL,
  describeTrigger,
  portsOf,
  saveFlowDraftRequestSchema,
  validateGraph,
  type FlowDetail,
  type FlowEdge,
  type FlowEnrolRequest,
  type FlowEnrolResponse,
  type FlowGraph,
  type FlowNode,
  type FlowNodeStats,
  type FlowPreflightCheck,
  type FlowPreflightResponse,
  type FlowSettings,
  type FlowStatsResponse,
  type FlowSuggestRequest,
  type FlowSuggestResponse,
  type FlowTestPeopleResponse,
  type FlowTestRequest,
  type FlowTestResponse,
  type SaveFlowDraftRequest,
} from '@wend/shared';
import { cn } from '@wend/ui';
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
  type PointerEvent as ReactPointerEvent,
} from 'react';

import { errorMessage } from '../../lib/api/errors';
import type { RunPeopleApi } from './RunView';
import { AddMenu, type AddMenuView, type AddPick } from './AddMenu';
import { Drawer } from './Drawer';
import { Edges, type FlowGeometry } from './Edges';
import { FlowList } from './FlowList';
import {
  IcChevron,
  IcClose,
  IcFit,
  IcFlask,
  IcMinus,
  IcPause,
  IcPencil,
  IcPlay,
  IcPlus,
  IcSend,
} from './icons';
import { EST_H, findSpot, inOffset, nodeWidth, SUGGEST_BAR_H, type Rect } from './layout';
import {
  connectableTargets,
  draftProblem,
  emailSpec,
  fmtIst,
  freshId,
  logicTemplateSpecs,
  n,
  nodeFromSpec,
  nodeName,
  reconcileEdges,
  savedAgo,
  specsFor,
  takenIds,
  testPathEdges,
  whatsappSpec,
  defaultBindings,
  type FlowEditorData,
  type NodeSpec,
  type WaTemplateShape,
} from './model';
import { NodeView, type FlowMode, type TestMark } from './Node';
import { Preflight } from './Preflight';
import { TestPanel } from './TestPanel';

/* -------------------------------------------------------------------------- */
/* What the editor needs from outside                                          */
/* -------------------------------------------------------------------------- */

/** The API, injected — so the page wires the real client and a verify suite can render with none. */
export interface FlowEditorApi {
  save: (flowId: string, request: SaveFlowDraftRequest) => Promise<FlowDetail>;
  preflight: (flowId: string) => Promise<FlowPreflightResponse>;
  launch: (flowId: string) => Promise<FlowDetail>;
  pause: (flowId: string) => Promise<FlowDetail>;
  resume: (flowId: string) => Promise<FlowDetail>;
  archive: (flowId: string, stopAll: boolean) => Promise<FlowDetail>;
  enrol: (flowId: string, request: FlowEnrolRequest) => Promise<FlowEnrolResponse>;
  suggest: (flowId: string, request: FlowSuggestRequest) => Promise<FlowSuggestResponse>;
  test: (flowId: string, request: FlowTestRequest, signal?: AbortSignal) => Promise<FlowTestResponse>;
  testPeople: (flowId: string, segmentId: string | null, signal?: AbortSignal) => Promise<FlowTestPeopleResponse>;
  stats: (flowId: string, version: number | null, signal?: AbortSignal) => Promise<FlowStatsResponse>;
  loadWaShape: (templateId: string) => Promise<WaTemplateShape | null>;
  enrollments: RunPeopleApi['enrollments'];
  enrollment: RunPeopleApi['enrollment'];
}

export interface FlowEditorProps {
  flow: FlowDetail;
  data: FlowEditorData;
  /** Templates that exist but are not approved — the add menu says how many it hides. */
  hiddenTemplates: number;
  api: FlowEditorApi;
  onClose: () => void;
  /** First mode; defaults to Run for a live flow with no draft, else Edit. */
  initialMode?: FlowMode;
}

/* -------------------------------------------------------------------------- */
/* Constants                                                                   */
/* -------------------------------------------------------------------------- */

const BASE_W = 1400;
const BASE_H = 760;
const MIN_SCALE = 0.85;
const MENU_W = 256;
const MENU_H = 440;
const AUTOSAVE_MS = 900;
const STATS_MS = 30_000;
const EMPTY_SET: ReadonlySet<string> = new Set();

const motion = (): ScrollBehavior =>
  typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth';

function initialScale(): number {
  if (typeof window === 'undefined') return 0.92;
  return window.innerWidth >= 1000 ? 0.92 : MIN_SCALE;
}

/**
 * True at 640px and up, where the canvas is drawn; below it the list is. The
 * drawer and the add menu mount in one of the two places, never both, so a
 * run-mode drawer does not fetch its people twice. Server render (the verify
 * suites) says wide.
 */
function useWide(): boolean {
  const query = '(min-width: 640px)';
  const [wide, setWide] = useState(() => (typeof window === 'undefined' || !window.matchMedia ? true : window.matchMedia(query).matches));
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const list = window.matchMedia(query);
    const on = () => setWide(list.matches);
    list.addEventListener?.('change', on);
    return () => list.removeEventListener?.('change', on);
  }, []);
  return wide;
}

/** The graph a mode shows, and the settings beside it. */
function editableOf(flow: FlowDetail): { graph: FlowGraph; settings: FlowSettings } | null {
  const version = flow.draft ?? flow.live;
  return version ? { graph: version.graph, settings: version.settings } : null;
}

/* -------------------------------------------------------------------------- */
/* The editor                                                                  */
/* -------------------------------------------------------------------------- */

export function FlowEditor({ flow: initialFlow, data: initialData, hiddenTemplates, api, onClose, initialMode }: FlowEditorProps) {
  const [flow, setFlow] = useState(initialFlow);
  const start = editableOf(initialFlow);
  const [graph, setGraph] = useState<FlowGraph>(start?.graph ?? { nodes: [], edges: [] });
  const [settings, setSettings] = useState<FlowSettings | null>(start?.settings ?? null);
  const [name, setName] = useState(initialFlow.name);
  const [mode, setMode] = useState<FlowMode>(initialMode ?? (initialFlow.live && !initialFlow.draft ? 'run' : 'edit'));
  const [waShapes, setWaShapes] = useState<ReadonlyMap<string, WaTemplateShape>>(initialData.waShapes);
  const wide = useWide();
  const data = useMemo<FlowEditorData>(() => ({ ...initialData, waShapes }), [initialData, waShapes]);

  // Autosave bookkeeping.
  const [revision, setRevision] = useState(0);
  const [savedRevision, setSavedRevision] = useState(0);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState<number | null>(initialFlow.draft ? Date.parse(initialFlow.draft.updatedAt) : null);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [problem, setProblem] = useState<{ message: string; nodeId: string | null } | null>(null);
  const [now, setNow] = useState(() => Date.now());

  // Canvas.
  const [selected, setSelected] = useState<string | null>(null);
  const [menu, setMenu] = useState<{ nodeId: string; portId: string; view: AddMenuView } | null>(null);
  const [scale, setScale] = useState(initialScale);
  const [geo, setGeo] = useState<FlowGeometry>({ ports: {}, ins: {}, sizes: {} });
  const [toast, setToast] = useState<string | null>(null);
  const [versionsOpen, setVersionsOpen] = useState(false);
  const [suggesting, setSuggesting] = useState(false);
  const [enrolling, setEnrolling] = useState(false);

  // Pre-flight.
  const [sheet, setSheet] = useState(false);
  const [pf, setPf] = useState<FlowPreflightResponse | null>(null);
  const [pfLoading, setPfLoading] = useState(false);
  const [pfError, setPfError] = useState<string | null>(null);
  const [launching, setLaunching] = useState(false);

  // Test.
  const [testPeople, setTestPeople] = useState<FlowTestPeopleResponse | null>(null);
  const [testPeopleError, setTestPeopleError] = useState<string | null>(null);
  const [personId, setPersonId] = useState<string | null>(null);
  const [walk, setWalk] = useState<FlowTestResponse | null>(null);
  const [walking, setWalking] = useState(false);
  const [walkError, setWalkError] = useState<string | null>(null);
  const [testIdx, setTestIdx] = useState(0);

  // Run.
  const [stats, setStats] = useState<FlowStatsResponse | null>(null);
  const [statsError, setStatsError] = useState<string | null>(null);

  const rootRef = useRef<HTMLDivElement>(null);
  const viewportRef = useRef<HTMLDivElement>(null);
  const canvasRef = useRef<HTMLDivElement>(null);
  const nameBtnRef = useRef<HTMLButtonElement>(null);
  const versionsRef = useRef<HTMLDivElement>(null);
  const geoKey = useRef('');
  const pendingFocus = useRef<string | null>(null);
  const toastTimer = useRef<number>(0);
  const saveTimer = useRef<number>(0);
  const sheetRef = useRef(false);
  sheetRef.current = sheet;
  const suppressClick = useRef(false);
  const latest = useRef({ graph, settings, name, revision });
  latest.current = { graph, settings, name, revision };
  const inFlight = useRef<Promise<boolean> | null>(null);
  const savedRevRef = useRef(0);
  const problemRef = useRef<{ message: string; nodeId: string | null } | null>(null);

  const live = flow.live;
  const running = mode === 'run' && live !== null;
  const shownGraph = running && live ? live.graph : graph;
  const shownSettings = running && live ? live.settings : settings;
  const dirty = revision !== savedRevision;
  const editingVersion = flow.draft?.version ?? (live ? live.version + 1 : 1);
  const isDraftOfLive = live !== null && (flow.draft !== null || dirty);

  /* ---- toast ------------------------------------------------------------- */
  const say = useCallback((message: string) => {
    if (sheetRef.current) return;
    setToast(message);
    window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast(null), 4200);
  }, []);
  useEffect(() => () => window.clearTimeout(toastTimer.current), []);

  /* ---- edits --------------------------------------------------------------- */
  const edit = useCallback((change: (graph: FlowGraph) => FlowGraph) => {
    setGraph((g) => change(g));
    setRevision((r) => r + 1);
  }, []);
  const editSettings = useCallback((next: FlowSettings) => {
    setSettings(next);
    setRevision((r) => r + 1);
  }, []);

  /* ---- autosave ------------------------------------------------------------ */
  const saveNow = useCallback(async (): Promise<boolean> => {
    const { graph: g, settings: s, name: nm, revision: rev } = latest.current;
    if (s === null) return false;
    const parsed = saveFlowDraftRequestSchema.safeParse({ graph: g, settings: s, name: nm });
    if (!parsed.success) {
      problemRef.current = draftProblem(parsed.error, g);
      setProblem(problemRef.current);
      return false;
    }
    const structural = validateGraph(g);
    if (structural.length > 0) {
      const first = structural[0];
      problemRef.current = { message: first?.message ?? 'The graph is not valid.', nodeId: first?.nodeId ?? null };
      setProblem(problemRef.current);
      return false;
    }
    problemRef.current = null;
    setProblem(null);
    setSaving(true);
    try {
      const next = await api.save(flow.id, parsed.data);
      setFlow(next);
      setSavedAt(Date.now());
      setSaveError(null);
      savedRevRef.current = Math.max(savedRevRef.current, rev);
      setSavedRevision(savedRevRef.current);
      return true;
    } catch (error) {
      setSaveError(errorMessage(error));
      return false;
    } finally {
      setSaving(false);
    }
  }, [api, flow.id]);

  useEffect(() => {
    if (!dirty) return undefined;
    window.clearTimeout(saveTimer.current);
    saveTimer.current = window.setTimeout(() => {
      inFlight.current = saveNow().finally(() => {
        inFlight.current = null;
      });
    }, AUTOSAVE_MS);
    return () => window.clearTimeout(saveTimer.current);
  }, [dirty, revision, saveNow]);

  /** Saves now if anything is pending; true when the server holds what is on screen. */
  const flush = useCallback(async (): Promise<boolean> => {
    window.clearTimeout(saveTimer.current);
    if (inFlight.current) await inFlight.current;
    if (latest.current.revision === savedRevRef.current && !problemRef.current) return true;
    return saveNow();
  }, [saveNow]);

  useEffect(() => {
    const id = window.setInterval(() => setNow(Date.now()), 5000);
    return () => window.clearInterval(id);
  }, []);

  /* ---- WhatsApp template shapes ------------------------------------------ */
  const shapeRequests = useRef(new Set<string>());
  const needShapes = useMemo(() => {
    const ids = new Set<string>();
    for (const g of [graph, live?.graph]) {
      for (const node of g?.nodes ?? []) {
        if (node.type === 'message' && node.data.channel === 'whatsapp' && node.data.templateId) ids.add(node.data.templateId);
      }
    }
    return [...ids].filter((id) => !waShapes.has(id));
  }, [graph, live, waShapes]);
  useEffect(() => {
    for (const id of needShapes) {
      if (shapeRequests.current.has(id)) continue;
      shapeRequests.current.add(id);
      void api.loadWaShape(id).then((shape) => {
        if (shape) setWaShapes((m) => new Map(m).set(id, shape));
      });
    }
  }, [needShapes, api]);

  // Keep each WhatsApp step's button snapshot and variables in step with its
  // template, only on a graph that is already a draft — never by itself turning
  // a live flow into a draft.
  useEffect(() => {
    if (flow.draft === null && live !== null) return;
    let changed = false;
    const nodes = graph.nodes.map((node) => {
      if (node.type !== 'message' || node.data.channel !== 'whatsapp' || !node.data.templateId) return node;
      const d = node.data;
      const shape = waShapes.get(node.data.templateId);
      if (!shape) return node;
      const sameButtons = shape.buttons.length === d.buttons.length && shape.buttons.every((b, i) => b === d.buttons[i]);
      const samePositions = shape.positions.length === d.vars.length && shape.positions.every((p) => d.vars.some((v) => v.position === p));
      if (sameButtons && samePositions) return node;
      changed = true;
      return { ...node, data: { ...d, buttons: shape.buttons, vars: defaultBindings(shape.positions, d.vars) } };
    });
    if (!changed) return;
    edit((g) => {
      let edges = g.edges;
      for (const node of nodes) edges = reconcileEdges(edges, node);
      return { nodes, edges };
    });
  }, [waShapes, graph, flow.draft, live, edit]);

  /* ---- measurement --------------------------------------------------------- */
  const [fontTick, setFontTick] = useState(0);
  useEffect(() => {
    let alive = true;
    void document.fonts?.ready?.then(() => {
      if (alive) setFontTick((t) => t + 1);
    });
    return () => {
      alive = false;
    };
  }, []);

  useLayoutEffect(() => {
    const c = canvasRef.current;
    if (!c) return;
    const cr = c.getBoundingClientRect();
    if (cr.width === 0) return;
    const ports: Record<string, { x: number; y: number }> = {};
    const ins: Record<string, { x: number; y: number }> = {};
    const sizes: Record<string, { w: number; h: number }> = {};
    const centre = (el: Element) => {
      const r = el.getBoundingClientRect();
      return { x: Math.round((r.left + r.width / 2 - cr.left) / scale), y: Math.round((r.top + r.height / 2 - cr.top) / scale) };
    };
    c.querySelectorAll<HTMLElement>('[data-port]').forEach((el) => {
      if (el.dataset.port) ports[el.dataset.port] = centre(el);
    });
    c.querySelectorAll<HTMLElement>('[data-in]').forEach((el) => {
      if (el.dataset.in) ins[el.dataset.in] = centre(el);
    });
    c.querySelectorAll<HTMLElement>('[data-node]').forEach((el) => {
      if (el.dataset.node) sizes[el.dataset.node] = { w: el.offsetWidth, h: el.offsetHeight };
    });
    const key = JSON.stringify([ports, ins, sizes]);
    if (key !== geoKey.current) {
      geoKey.current = key;
      setGeo({ ports, ins, sizes });
    }
  });
  void fontTick;

  // Focus requests that need the DOM after a state change.
  useEffect(() => {
    if (pendingFocus.current) {
      // The first VISIBLE match: the canvas and the phone list carry the same
      // hooks, and below 640px the canvas is display:none — focusing its copy
      // did nothing and dropped focus to <body> (slice 71 review).
      const matches = [...(rootRef.current?.querySelectorAll<HTMLElement>(pendingFocus.current) ?? [])];
      const el = matches.find((one) => one.offsetParent !== null || one.getClientRects().length > 0) ?? matches[0];
      pendingFocus.current = null;
      el?.focus({ preventScroll: false });
    }
  });

  /* ---- derived ------------------------------------------------------------- */
  const W = Math.max(BASE_W, ...shownGraph.nodes.map((x) => x.position.x + (geo.sizes[x.id]?.w ?? nodeWidth(x.type)) + 160));
  const H = Math.max(BASE_H, ...shownGraph.nodes.map((x) => x.position.y + (geo.sizes[x.id]?.h ?? EST_H[x.type]) + 60));

  const connected = useMemo(() => {
    const m = new Map<string, Set<string>>();
    for (const e of shownGraph.edges) {
      const set = m.get(e.from) ?? new Set<string>();
      set.add(e.port);
      m.set(e.from, set);
    }
    return m;
  }, [shownGraph.edges]);
  const suggestedIds = useMemo(() => new Set(graph.nodes.filter((x) => x.suggested).map((x) => x.id)), [graph.nodes]);
  const statsByNode = useMemo(() => (stats && running ? new Map<string, FlowNodeStats>(stats.nodes.map((s) => [s.nodeId, s])) : null), [stats, running]);
  const volumes = useMemo(() => (stats && running ? new Map(stats.edges.map((e) => [e.edgeId, e.volume])) : null), [stats, running]);

  const segmentName = (id: string | null) => (id ? (data.segments.find((s) => s.id === id)?.name ?? null) : null);
  const triggerSummary = shownSettings
    ? describeTrigger(shownSettings.trigger, segmentName(shownSettings.trigger.type === 'segment_entry' ? shownSettings.trigger.segmentId : shownSettings.audienceSegmentId))
    : '';

  /* ---- test mode ----------------------------------------------------------- */
  const testSegment = settings ? (settings.trigger.type === 'segment_entry' ? settings.trigger.segmentId : settings.audienceSegmentId) : null;
  useEffect(() => {
    if (mode !== 'test') return undefined;
    const controller = new AbortController();
    setTestPeopleError(null);
    api.testPeople(flow.id, testSegment, controller.signal).then(
      (result) => {
        setTestPeople(result);
        setPersonId((current) => (current && result.people.some((p) => p.id === current) ? current : (result.people[0]?.id ?? null)));
      },
      (error: unknown) => {
        if (!controller.signal.aborted) setTestPeopleError(errorMessage(error));
      },
    );
    return () => controller.abort();
  }, [mode, api, flow.id, testSegment]);

  const testVersion: 'draft' | 'live' = flow.draft || !live ? 'draft' : 'live';
  useEffect(() => {
    if (mode !== 'test' || personId === null) return undefined;
    const controller = new AbortController();
    let cancelled = false;
    setWalking(true);
    setWalkError(null);
    void (async () => {
      const saved = testVersion === 'live' || (await flush());
      if (cancelled) return;
      if (!saved) {
        setWalking(false);
        setWalkError('Save the draft first — fix what the top bar names, then the walk runs.');
        return;
      }
      try {
        const result = await api.test(flow.id, { personId, version: flow.draft || !live ? 'draft' : 'live' }, controller.signal);
        if (cancelled) return;
        setWalk(result);
        setTestIdx(0);
      } catch (error) {
        if (!cancelled) setWalkError(errorMessage(error));
      } finally {
        if (!cancelled) setWalking(false);
      }
    })();
    return () => {
      cancelled = true;
      controller.abort();
    };
    // Re-walk when the person changes or a save lands.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, personId, savedAt]);

  const testState = useMemo(() => {
    if (mode !== 'test' || !walk) return null;
    const steps = walk.steps;
    const idx = Math.min(testIdx, Math.max(0, steps.length - 1));
    const pathNodes = new Set(steps.slice(0, idx + 1).flatMap((s) => (s.nodeId ? [s.nodeId] : [])));
    const pathEdges = testPathEdges(graph, steps.slice(0, idx));
    return { idx, pathNodes, pathEdges, current: steps[idx]?.nodeId ?? null };
  }, [mode, walk, testIdx, graph]);

  const testMark = (id: string): TestMark => {
    if (!testState) return null;
    if (testState.current === id) return 'current';
    return testState.pathNodes.has(id) ? 'path' : 'off';
  };

  /* ---- run mode ------------------------------------------------------------ */
  useEffect(() => {
    if (!running || !live) return undefined;
    const controller = new AbortController();
    const load = () =>
      api.stats(flow.id, live.version, controller.signal).then(
        (result) => {
          setStats(result);
          setStatsError(null);
        },
        (error: unknown) => {
          if (!controller.signal.aborted) setStatsError(errorMessage(error));
        },
      );
    void load();
    const id = window.setInterval(() => void load(), STATS_MS);
    return () => {
      controller.abort();
      window.clearInterval(id);
    };
  }, [running, live, api, flow.id]);

  /* ---- menu ---------------------------------------------------------------- */
  const openMenu = useCallback((nodeId: string, portId: string) => {
    setMenu((m) => (m && m.nodeId === nodeId && m.portId === portId ? null : { nodeId, portId, view: 'root' }));
    setSelected(null);
  }, []);
  const closeMenu = useCallback((returnFocus: boolean) => {
    setMenu((m) => {
      if (m && returnFocus) pendingFocus.current = `[data-addport="${m.nodeId}:${m.portId}"]`;
      return null;
    });
  }, []);
  useEffect(() => {
    if (!menu) return undefined;
    const onDown = (event: PointerEvent) => {
      const target = event.target as Element | null;
      if (target?.closest?.('[data-flow-menu]') || target?.closest?.('[data-addport]')) return;
      setMenu(null);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [menu]);

  const menuNode = menu ? graph.nodes.find((x) => x.id === menu.nodeId) : undefined;
  const menuShape = menuNode && menuNode.type === 'message' && menuNode.data.channel === 'whatsapp' && menuNode.data.templateId ? waShapes.get(menuNode.data.templateId) : undefined;
  const menuPort = menuNode && menu ? portsOf(menuNode, menuShape ?? null).find((p) => p.id === menu.portId) : undefined;
  const menuAt = menu ? geo.ports[`${menu.nodeId}:${menu.portId}`] : undefined;
  const menuScreen = menuAt
    ? { left: Math.round((menuAt.x + 30) * scale), top: Math.round(Math.max(12, Math.min(menuAt.y * scale - 26, Math.max(12, H * scale - MENU_H)))) }
    : null;
  const canvasW = menuAt ? Math.max(W, menuAt.x + 30 + (MENU_W + 40) / scale) : W;
  const targets = useMemo(
    () => (menu ? connectableTargets(graph, menu.nodeId).map((node) => ({ id: node.id, label: `${nodeName(node)}${node.type === 'message' && node.data.templateId ? ` · ${data.templates.find((t) => t.id === node.data.templateId)?.name ?? ''}` : ''}` })) : []),
    [menu, graph, data.templates],
  );

  /* ---- inserting ------------------------------------------------------------ */
  const occupied = useCallback(
    (g: FlowGraph): Rect[] =>
      g.nodes.map((x) => ({
        x: x.position.x,
        y: x.position.y - (x.suggested ? SUGGEST_BAR_H : 0),
        w: geo.sizes[x.id]?.w ?? nodeWidth(x.type),
        h: (geo.sizes[x.id]?.h ?? EST_H[x.type]) + (x.suggested ? SUGGEST_BAR_H : 0),
      })),
    [geo.sizes],
  );

  const placeChain = useCallback(
    (g: FlowGraph, fromId: string, portId: string, specs: NodeSpec[]): { graph: FlowGraph; first: FlowNode | null } => {
      const at = geo.ports[`${fromId}:${portId}`] ?? (() => {
        const from = g.nodes.find((x) => x.id === fromId);
        return from ? { x: from.position.x + nodeWidth(from.type), y: from.position.y + 40 } : { x: 40, y: 40 };
      })();
      const taken = takenIds(g);
      const rects = occupied(g);
      const created: FlowNode[] = [];
      const edges: FlowEdge[] = [];
      let prev: FlowNode | null = null;
      let x0 = at.x + 74;
      let yIn = at.y;
      for (const spec of specs) {
        const id = freshId(spec.type.slice(0, 3), taken);
        taken.add(id);
        if (spec.type === 'comment') {
          const pos = findSpot(rects, 'comment', at.x + 40, at.y + 60, false);
          created.push(nodeFromSpec(spec, id, pos));
          continue;
        }
        const pos = findSpot(rects, spec.type, x0, yIn, spec.suggested !== undefined);
        const node = nodeFromSpec(spec, id, pos);
        created.push(node);
        const edgeId = freshId('e', taken);
        taken.add(edgeId);
        const fromPort: string = prev ? (portsOf(prev)[0]?.id ?? 'out') : portId;
        edges.push({ id: edgeId, from: prev ? prev.id : fromId, port: fromPort, to: id });
        prev = node;
        x0 = pos.x + nodeWidth(spec.type) + 80;
        yIn = pos.y + inOffset(spec.type);
      }
      return { graph: { nodes: [...g.nodes, ...created], edges: [...g.edges, ...edges] }, first: created[0] ?? null };
    },
    [geo.ports, occupied],
  );

  const scrollTo = useCallback(
    (node: FlowNode) => {
      const vp = viewportRef.current;
      if (!vp) return;
      const left = node.position.x * scale;
      const right = (node.position.x + nodeWidth(node.type)) * scale;
      const drawerW = vp.clientWidth > 760 ? 380 : 0;
      if (left < vp.scrollLeft + 20 || right > vp.scrollLeft + vp.clientWidth - drawerW - 20) {
        vp.scrollTo?.({ left: Math.max(0, right - vp.clientWidth + drawerW + 60), behavior: motion() });
      }
      const top = node.position.y * scale;
      if (top < vp.scrollTop + 10 || top > vp.scrollTop + vp.clientHeight - 160) {
        vp.scrollTo?.({ top: Math.max(0, top - 80), behavior: motion() });
      }
    },
    [scale],
  );

  const insert = useCallback(
    async (pick: AddPick) => {
      if (!menu) return;
      const { nodeId, portId } = menu;
      const taken = takenIds(graph);
      const idFor = (prefix: string) => {
        const id = freshId(prefix, taken);
        taken.add(id);
        return id;
      };

      if (pick.kind === 'connect') {
        edit((g) => ({ ...g, edges: [...g.edges, { id: freshId('e', takenIds(g)), from: nodeId, port: portId, to: pick.to }] }));
        setMenu(null);
        pendingFocus.current = `[data-node="${pick.to}"] [data-head]`;
        return;
      }

      if (pick.kind === 'suggest') {
        setSuggesting(true);
        try {
          if (!(await flush())) {
            say('Save the draft first — the top bar says what is holding it back.');
            return;
          }
          const result = await api.suggest(flow.id, { afterNodeId: nodeId, port: portId });
          if (!result.suggestion) {
            setMenu(null);
            say(result.reason);
            return;
          }
          const { id: _ignored, position: _pos, ...rest } = result.suggestion.node;
          void _ignored;
          void _pos;
          const current = latest.current.graph;
          if (current.edges.some((e) => e.from === nodeId && e.port === portId)) {
            setMenu(null);
            return;
          }
          const placed = placeChain(current, nodeId, portId, [rest as NodeSpec]);
          edit(() => placed.graph);
          setMenu(null);
          if (placed.first) {
            pendingFocus.current = `[data-node="${placed.first.id}"] [data-sugok]`;
            scrollTo(placed.first);
          }
          say('Suggested a step — accept ✓ or reject ✕. Suggestions never launch on their own.');
        } catch (error) {
          say(errorMessage(error));
        } finally {
          setSuggesting(false);
        }
        return;
      }

      let specs: NodeSpec[];
      if (pick.kind === 'message') {
        specs = [pick.channel === 'email' ? emailSpec(pick.templateId) : whatsappSpec(pick.templateId, pick.templateId ? waShapes.get(pick.templateId) : undefined)];
      } else if (pick.kind === 'logictpl') {
        specs = logicTemplateSpecs(pick.id, idFor);
      } else {
        specs = specsFor(pick.step, idFor);
      }
      const placed = placeChain(graph, nodeId, portId, specs);
      edit(() => placed.graph);
      setMenu(null);
      if (placed.first) {
        setSelected(placed.first.id);
        scrollTo(placed.first);
      }
    },
    [menu, graph, edit, flush, api, flow.id, say, placeChain, waShapes, scrollTo],
  );

  const onSelect = useCallback(
    (id: string) => {
      if (suppressClick.current) return;
      if (mode === 'test') {
        const i = walk?.steps.findIndex((s) => s.nodeId === id) ?? -1;
        if (i >= 0) setTestIdx(i);
        return;
      }
      setMenu(null);
      setSelected((s) => (s === id && mode !== 'edit' ? null : id));
    },
    [mode, walk],
  );

  const replaceNode = useCallback(
    (node: FlowNode) => {
      edit((g) => ({ nodes: g.nodes.map((x) => (x.id === node.id ? node : x)), edges: reconcileEdges(g.edges, node) }));
    },
    [edit],
  );

  const removeNode = useCallback(
    (id: string) => {
      const inc = graph.edges.find((e) => e.to === id);
      if (inc && !sheetRef.current) pendingFocus.current = `[data-addport="${inc.from}:${inc.port}"]`;
      edit((g) => ({ nodes: g.nodes.filter((x) => x.id !== id), edges: g.edges.filter((e) => e.from !== id && e.to !== id) }));
      setSelected((s) => (s === id ? null : s));
    },
    [graph.edges, edit],
  );

  const onAccept = useCallback(
    (id: string) => {
      edit((g) => ({
        ...g,
        nodes: g.nodes.map((x) => {
          if (x.id !== id) return x;
          const { suggested: _s, ...rest } = x;
          void _s;
          return rest as FlowNode;
        }),
      }));
      if (!sheetRef.current) pendingFocus.current = `[data-node="${id}"] [data-head]`;
      say('Accepted — it is a normal step now. Edit it like any other.');
    },
    [edit, say],
  );
  const onReject = useCallback(
    (id: string) => {
      removeNode(id);
      say('Suggestion removed.');
    },
    [removeNode, say],
  );

  /* ---- dragging steps ----------------------------------------------------- */
  const onDragStart = useCallback(
    (id: string, event: ReactPointerEvent<HTMLDivElement>) => {
      if (mode !== 'edit' || event.button !== 0) return;
      const target = event.target as Element;
      if (target.closest('[data-port],[data-noclick],select,input,textarea')) return;
      const node = graph.nodes.find((x) => x.id === id);
      if (!node) return;
      const startX = event.clientX;
      const startY = event.clientY;
      const orig = node.position;
      let moved = false;
      const onMove = (e: PointerEvent) => {
        const dx = (e.clientX - startX) / scale;
        const dy = (e.clientY - startY) / scale;
        if (!moved && Math.abs(dx) + Math.abs(dy) < 4) return;
        if (!moved) {
          moved = true;
          setRevision((r) => r + 1);
        }
        const position = { x: Math.max(0, Math.round(orig.x + dx)), y: Math.max(0, Math.round(orig.y + dy)) };
        setGraph((g) => ({ ...g, nodes: g.nodes.map((x) => (x.id === id ? { ...x, position } : x)) }));
      };
      const onUp = () => {
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        if (moved) {
          setRevision((r) => r + 1);
          suppressClick.current = true;
          window.setTimeout(() => {
            suppressClick.current = false;
          }, 0);
        }
      };
      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
    },
    [mode, graph.nodes, scale],
  );

  /* ---- panning and zoom ---------------------------------------------------- */
  const pan = useRef<{ x: number; y: number; l: number; t: number; moved: boolean } | null>(null);
  const onPointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if (event.button !== 0) return;
    const target = event.target as Element;
    if (!(target === viewportRef.current || target === canvasRef.current || target.closest('[data-sizer]') === target || target.closest('[data-role="flow-edges"]'))) return;
    const vp = viewportRef.current;
    if (!vp) return;
    pan.current = { x: event.clientX, y: event.clientY, l: vp.scrollLeft, t: vp.scrollTop, moved: false };
    vp.setPointerCapture?.(event.pointerId);
  };
  const onPointerMove = (event: ReactPointerEvent<HTMLDivElement>) => {
    const p = pan.current;
    const vp = viewportRef.current;
    if (!p || !vp) return;
    const dx = event.clientX - p.x;
    const dy = event.clientY - p.y;
    if (Math.abs(dx) + Math.abs(dy) > 4) p.moved = true;
    vp.scrollLeft = p.l - dx;
    vp.scrollTop = p.t - dy;
  };
  const onPointerUp = () => {
    const p = pan.current;
    pan.current = null;
    if (p && !p.moved && mode !== 'test') {
      setSelected(null);
      setMenu(null);
    }
  };
  const zoom = (z: number | 'fit') =>
    setScale((s) => {
      if (z === 'fit') return Math.max(0.45, Math.min(1.15, Math.floor(((viewportRef.current?.clientWidth ?? 1000) / W) * 100) / 100));
      const next = Math.round((s + z) * 100) / 100;
      return Math.min(1.15, z < 0 ? Math.max(Math.min(s, MIN_SCALE), next) : next);
    });
  const onViewportKey = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.target !== viewportRef.current) return;
    if (event.key === '+' || event.key === '=') zoom(0.1);
    else if (event.key === '-') zoom(-0.1);
    else if (event.key === '0') zoom('fit');
  };

  /* ---- modes, launch, lifecycle ------------------------------------------- */
  const switchMode = (next: FlowMode) => {
    if (next === mode) return;
    if (next === 'run' && !live) {
      say('Launch first to see it run.');
      return;
    }
    setMenu(null);
    setSelected(null);
    setVersionsOpen(false);
    if (next === 'edit' && live && !flow.draft) {
      say(`Editing Version ${live.version + 1} as a draft. Version ${live.version} keeps running untouched.`);
      setGraph(live.graph);
      setSettings(live.settings);
    }
    if (next === 'test') setTestIdx(0);
    setMode(next);
  };

  const runPreflight = useCallback(async () => {
    setPfLoading(true);
    setPfError(null);
    try {
      if (!(await flush())) {
        setPf(null);
        setPfError('The draft cannot be saved yet, so it cannot be checked. Fix what the top bar names first.');
        return;
      }
      setPf(await api.preflight(flow.id));
    } catch (error) {
      setPfError(errorMessage(error));
    } finally {
      setPfLoading(false);
    }
  }, [flush, api, flow.id]);

  const openSheet = () => {
    setMenu(null);
    setSheet(true);
    setPf(null);
    void runPreflight();
  };
  const closeSheet = () => {
    pendingFocus.current = '[data-role="flow-primary"]';
    setSheet(false);
  };

  const launch = async () => {
    setLaunching(true);
    const relaunch = live !== null;
    try {
      const next = await api.launch(flow.id);
      setFlow(next);
      if (next.draft === null && next.live) {
        setGraph(next.live.graph);
        setSettings(next.live.settings);
      }
      setSheet(false);
      sheetRef.current = false;
      setSelected(null);
      setMode('run');
      pendingFocus.current = '[data-role="flow-primary"]';
      say(
        relaunch
          ? `Version ${next.live?.version ?? editingVersion} published. New entrants get it; people already inside finish on the version they entered.`
          : `Live — Version ${next.live?.version ?? 1}. Quiet hours are respected; nothing sends inside them.`,
      );
    } catch (error) {
      setPfError(errorMessage(error));
      void runPreflight();
    } finally {
      setLaunching(false);
    }
  };

  const togglePause = async () => {
    try {
      const next = flow.status === 'paused' ? await api.resume(flow.id) : await api.pause(flow.id);
      setFlow(next);
      say(next.status === 'paused' ? 'Paused. No new entries; people hold at their current step.' : 'Resumed. People move on and new entrants flow in again.');
    } catch (error) {
      say(errorMessage(error));
    }
  };

  const archive = async () => {
    if (typeof window !== 'undefined' && !window.confirm('Archive this flow? Nobody new enters; people already inside finish their path.')) return;
    try {
      setFlow(await api.archive(flow.id, false));
      setVersionsOpen(false);
      say('Archived. Nobody new enters; people already inside finish.');
    } catch (error) {
      say(errorMessage(error));
    }
  };

  const enrol = async (segmentId: string) => {
    setEnrolling(true);
    try {
      const result = await api.enrol(flow.id, { segmentId });
      const skipped = result.skipped.reduce((a, s) => a + s.count, 0);
      say(`Enrolled ${n(result.enrolled)}. ${n(result.alreadyEnrolled)} were already in${skipped ? `, ${n(skipped)} skipped` : ''}.`);
    } catch (error) {
      say(errorMessage(error));
    } finally {
      setEnrolling(false);
    }
  };

  const fixCheck = (check: FlowPreflightCheck) => {
    setSheet(false);
    setMode('edit');
    if (check.fix?.port && check.fix.nodeId) {
      setMenu(null);
      pendingFocus.current = `[data-addport="${check.fix.nodeId}:${check.fix.port}"]`;
    } else if (check.fix?.nodeId) {
      setSelected(check.fix.nodeId);
    }
  };

  const close = async () => {
    await flush();
    onClose();
  };

  /* ---- versions popover ---------------------------------------------------- */
  useEffect(() => {
    if (!versionsOpen) return undefined;
    versionsRef.current?.querySelector<HTMLElement>('input,button')?.focus();
    const onDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (versionsRef.current?.contains(target) || nameBtnRef.current?.contains(target)) return;
      setVersionsOpen(false);
    };
    document.addEventListener('pointerdown', onDown);
    return () => document.removeEventListener('pointerdown', onDown);
  }, [versionsOpen]);

  /* ---- render --------------------------------------------------------------- */
  if (!settings || !shownSettings) {
    return (
      <div className="grid min-h-dvh place-items-center bg-surface-1 p-6 text-center text-muted">
        This flow has no version to edit. Close the editor and create it again from a template.
      </div>
    );
  }

  const selNode = selected ? shownGraph.nodes.find((x) => x.id === selected) : undefined;
  const paused = flow.status === 'paused';
  const archived = flow.status === 'archived';

  let primaryLabel = 'Launch';
  let primaryOff = false;
  if (mode === 'run') {
    primaryLabel = archived ? 'Archived' : paused ? 'Resume' : 'Pause';
    primaryOff = archived;
  } else if (live && isDraftOfLive) {
    primaryLabel = `Publish v${editingVersion}`;
  } else if (live) {
    primaryLabel = `Live · v${live.version}`;
    primaryOff = true;
  }
  if (archived && mode !== 'run') {
    primaryLabel = 'Archived';
    primaryOff = true;
  }
  const PrimaryIcon = mode === 'run' ? (paused ? IcPlay : IcPause) : IcSend;

  const saveText = problem
    ? `Not saved — ${problem.message}`
    : saveError
      ? `Not saved — ${saveError}`
      : saving
        ? 'Saving…'
        : dirty
          ? 'Unsaved changes'
          : savedAgo(savedAt, now);

  const drawer = selNode && mode !== 'test' && shownSettings ? (
    <Drawer
      node={selNode}
      mode={mode}
      graph={shownGraph}
      settings={shownSettings}
      data={data}
      stat={statsByNode?.get(selNode.id) ?? null}
      flowId={flow.id}
      runVersion={running && live ? live.version : null}
      peopleApi={api}
      problem={problem && problem.nodeId === selNode.id ? problem.message : null}
      canEnrol={live !== null && !paused && !archived}
      enrolling={enrolling}
      onEnrol={(id) => void enrol(id)}
      onReplace={replaceNode}
      onSettings={editSettings}
      onDelete={removeNode}
      onDisconnect={(edgeId) => edit((g) => ({ ...g, edges: g.edges.filter((e) => e.id !== edgeId) }))}
      onClose={() => {
        pendingFocus.current = `[data-node="${selNode.id}"] [data-head]`;
        setSelected(null);
      }}
      newId={(prefix) => freshId(prefix, takenIds(graph))}
    />
  ) : null;

  const addMenu = (inline: boolean) =>
    menu && menuPort ? (
      <AddMenu
        x={inline || !menuScreen ? undefined : menuScreen.left}
        y={inline || !menuScreen ? undefined : menuScreen.top}
        view={menu.view}
        setView={(view) => setMenu((m) => (m ? { ...m, view } : m))}
        onPick={(pick) => void insert(pick)}
        onClose={closeMenu}
        portLabel={menuPort.label}
        templates={data.templates}
        hiddenTemplates={hiddenTemplates}
        targets={targets}
        suggesting={suggesting}
      />
    ) : null;

  const k = stats?.totals;
  const holdout = stats?.holdout ?? null;

  return (
    <div
      ref={rootRef}
      role="region"
      aria-label={`Flow editor — ${name}`}
      data-mode={mode}
      className="fixed inset-0 z-40 flex flex-col overflow-hidden bg-surface-1 text-[14px] leading-[1.45] text-text"
    >
      {/* ---------------- top bar ---------------- */}
      <header className="relative z-30 flex flex-wrap items-center gap-2.5 border-b border-white/[0.07] bg-[rgba(4,3,12,.94)] px-3 py-[9px]">
        <button
          type="button"
          aria-label="Close editor"
          onClick={() => void close()}
          className="flex cursor-pointer items-center gap-2.5 rounded-full border-0 bg-transparent py-[3px] pl-[3px] pr-2.5 text-[14px] font-medium text-white hover:bg-white/[0.06] focus-visible:outline-2 focus-visible:outline-brand-b"
        >
          <span className="grid size-[30px] place-items-center rounded-full bg-white/10">
            <IcClose size={14} />
          </span>
          <span className="hidden sm:inline" aria-hidden="true">Close editor</span>
        </button>
        <span className="hidden h-7 w-px bg-white/10 sm:block" aria-hidden="true" />
        <span
          data-role="flow-state"
          className={cn(
            'inline-flex items-center gap-2 rounded-full border px-3 py-1 text-[13.5px] font-medium',
            running && !paused && 'border-good/35 bg-good/10 text-good',
            running && paused && 'border-warn/35 bg-warn/10 text-warn',
            !running && 'border-white/10 bg-white/[0.08] pr-1 text-white',
          )}
        >
          {running ? (
            paused ? (
              'Paused'
            ) : archived ? (
              'Archived'
            ) : (
              <>
                <span aria-hidden="true" className="size-2 rounded-full bg-good shadow-[0_0_0_3px_rgba(61,214,140,.25)] motion-safe:animate-pulse" />
                Live
              </>
            )
          ) : mode === 'test' ? (
            'Testing'
          ) : (
            'Editing'
          )}
          {!running && (
            <span className="grid size-[22px] place-items-center rounded-full bg-[linear-gradient(135deg,#6D5EF6,#9B5EF6)] text-white">
              {mode === 'test' ? <IcFlask size={12} /> : <IcPencil size={12} />}
            </span>
          )}
        </span>
        <span className={cn('hidden max-w-[34ch] truncate text-[12.5px] md:inline', problem || saveError ? 'text-warn' : 'text-muted')} aria-live="polite" data-role="flow-saved">
          {running && live ? `Version ${live.version} running` : saveText}
        </span>

        <div
          className="relative order-10 mx-auto flex min-w-0 basis-full justify-center md:order-none md:basis-auto"
          onKeyDown={(event) => {
            if (event.key === 'Escape' && versionsOpen) {
              event.stopPropagation();
              setVersionsOpen(false);
              nameBtnRef.current?.focus();
            }
          }}
        >
          <button
            type="button"
            ref={nameBtnRef}
            aria-expanded={versionsOpen}
            aria-haspopup="dialog"
            aria-controls="flow-versions"
            onClick={() => setVersionsOpen((o) => !o)}
            className="flex max-w-full cursor-pointer items-center gap-1.5 rounded-lg border-0 bg-transparent px-2.5 py-1.5 text-[15px] font-medium text-white hover:bg-white/[0.06] focus-visible:outline-2 focus-visible:outline-brand-b"
          >
            <span className="truncate">{name}</span>
            <IcChevron size={14} />
          </button>
          {versionsOpen && (
            <div
              id="flow-versions"
              role="dialog"
              aria-label="Name and versions"
              ref={versionsRef}
              className="absolute left-1/2 top-[calc(100%+8px)] z-40 flex w-[300px] -translate-x-1/2 flex-col gap-1.5 rounded-xl border border-line-strong bg-surface-2 p-3 shadow-[0_24px_50px_-16px_rgba(0,0,0,.85)]"
            >
              <label className="flex flex-col gap-1">
                <span className="font-mono text-[10.5px] uppercase tracking-[0.11em] text-muted">Name</span>
                <input
                  className="rounded-lg border border-line-strong bg-white/5 px-2.5 py-1.5 text-[13.5px] text-text focus-visible:outline-2 focus-visible:outline-brand-b"
                  value={name}
                  maxLength={120}
                  disabled={mode !== 'edit'}
                  onChange={(event) => {
                    setName(event.target.value);
                    setRevision((r) => r + 1);
                  }}
                />
              </label>
              <p className="m-0 mt-1 font-mono text-[10.5px] uppercase tracking-[0.11em] text-muted">Versions</p>
              {flow.draft === null && isDraftOfLive && (
                <div className="flex justify-between gap-2.5 rounded-lg bg-brand-a/16 px-2 py-1.5 text-[13px] text-muted">
                  <b className="whitespace-nowrap font-medium text-white">Version {editingVersion}</b>
                  <span className="text-right">Draft · saving</span>
                </div>
              )}
              {[...flow.versions].sort((a, b) => b.version - a.version).map((v) => (
                <div key={v.id} className={cn('flex justify-between gap-2.5 rounded-lg px-2 py-1.5 text-[13px] text-muted', v.status !== 'retired' && 'bg-brand-a/16')}>
                  <b className="whitespace-nowrap font-medium text-white">Version {v.version}</b>
                  <span className="text-right">
                    {v.status === 'draft'
                      ? 'Draft · editing'
                      : v.status === 'live'
                        ? `${paused ? 'Paused' : 'Live'} since ${v.launchedAt ? fmtIst(v.launchedAt, false) : '—'}`
                        : `Ran ${v.launchedAt ? fmtIst(v.launchedAt, false) : '—'} – ${v.retiredAt ? fmtIst(v.retiredAt, false) : '—'}`}
                  </span>
                </div>
              ))}
              <p className="m-0 text-[12px] text-muted">Status: {FLOW_STATUS_LABEL[flow.status]}</p>
              <div className="mt-1 flex flex-wrap gap-2">
                {live && !archived && (
                  <button type="button" onClick={() => void archive()} className="cursor-pointer rounded-[9px] border border-bad/35 px-3 py-1.5 text-[13px] text-bad focus-visible:outline-2 focus-visible:outline-brand-b">
                    Archive flow
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => {
                    setVersionsOpen(false);
                    nameBtnRef.current?.focus();
                  }}
                  className="ml-auto cursor-pointer rounded-[9px] border border-line-strong bg-white/5 px-3 py-1.5 text-[13px] focus-visible:outline-2 focus-visible:outline-brand-b"
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>

        <span className="hidden text-[12.5px] text-muted md:inline" data-role="flow-version">
          Version {running && live ? live.version : editingVersion}
        </span>
        <div role="group" aria-label="Editor mode" className="ml-auto flex rounded-full border border-white/[0.08] bg-white/[0.06] p-[3px] md:ml-0">
          {(['edit', 'test', 'run'] as const).map((m) => {
            const off = m === 'run' && !live;
            return (
              <button
                key={m}
                type="button"
                aria-pressed={mode === m}
                aria-disabled={off || undefined}
                aria-describedby={off ? 'flow-run-hint' : undefined}
                onClick={() => switchMode(m)}
                className={cn(
                  'cursor-pointer rounded-full border-0 bg-transparent px-3 py-1.5 text-[13px] font-medium text-muted focus-visible:outline-2 focus-visible:outline-brand-b',
                  mode === m && 'bg-white/15 text-white',
                  off && 'cursor-not-allowed opacity-45',
                )}
              >
                {m === 'edit' ? 'Edit' : m === 'test' ? 'Test' : 'Run'}
              </button>
            );
          })}
          <span id="flow-run-hint" className="sr-only">Run is available after the flow is launched.</span>
        </div>
        <button
          type="button"
          data-role="flow-primary"
          disabled={primaryOff}
          onClick={() => (mode === 'run' ? void togglePause() : openSheet())}
          title={primaryOff && live ? `Version ${live.version} is live. Make a change in Edit to publish a new version.` : undefined}
          className={cn(
            'inline-flex cursor-pointer items-center gap-2 rounded-[10px] border-0 bg-white px-4 py-2 text-[14px] font-semibold text-ink hover:bg-[#EDE9FF] focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-b',
            mode === 'run' && 'border border-white/18 bg-white/10 text-white hover:bg-white/15',
            primaryOff && 'cursor-default border border-good/35 bg-good/12 text-good hover:bg-good/12',
          )}
        >
          <span>{primaryLabel}</span>
          {!primaryOff && <PrimaryIcon size={15} />}
        </button>
      </header>

      {/* ---------------- run header ---------------- */}
      {running && (
        <div className="grid gap-2.5 border-b border-line bg-white/[0.02] p-3 lg:grid-cols-[1fr_minmax(240px,330px)]" data-role="flow-runbar">
          <div className="grid grid-cols-2 gap-2 md:grid-cols-4">
            <div className="flex min-w-0 flex-col gap-px rounded-[10px] border border-line bg-white/[0.04] px-[11px] py-[9px]">
              <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted">Entered</span>
              <b className="text-[1.3rem] font-semibold tabular-nums text-white" aria-live="polite">{k ? n(k.entered) : '—'}</b>
              <small className="text-[11.5px] text-muted">Version {live?.version}</small>
            </div>
            <div className="flex min-w-0 flex-col gap-px rounded-[10px] border border-line bg-white/[0.04] px-[11px] py-[9px]">
              <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted">Active now</span>
              <b className="text-[1.3rem] font-semibold tabular-nums text-white">{k ? n(k.active + k.waiting) : '—'}</b>
              <small className="text-[11.5px] text-muted">{paused ? 'Holding — paused' : k ? `${n(k.waiting)} waiting` : ''}</small>
            </div>
            <div className="flex min-w-0 flex-col gap-px rounded-[10px] border border-line bg-white/[0.04] px-[11px] py-[9px]">
              <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted">Converted</span>
              <b className="text-[1.3rem] font-semibold tabular-nums text-good">{k ? n(k.converted) : '—'}</b>
              <small className="text-[11.5px] text-muted">{k ? `${n(k.completed)} finished${k.heldOut > 0 ? ` · ${n(k.heldOut)} held back` : ''} · ${n(k.exited)} left early` : ''}</small>
            </div>
            <div className="flex min-w-0 flex-col gap-px rounded-[10px] border border-line bg-white/[0.04] px-[11px] py-[9px]" data-role="flow-holdout">
              <span className="font-mono text-[10px] uppercase tracking-[0.1em] text-muted">Against the holdout</span>
              {holdout === null ? (
                <>
                  <b className="text-[1.3rem] font-semibold text-white">—</b>
                  <small className="text-[11.5px] text-muted">No holdout reserved</small>
                </>
              ) : holdout.readable && holdout.lift !== null ? (
                <>
                  <b className="text-[1.3rem] font-semibold tabular-nums text-brand-ink">{`${holdout.lift >= 0 ? '+' : ''}${Math.round(holdout.lift * 100)}% lift`}</b>
                  <small className="text-[11.5px] text-muted">{n(holdout.treatedConverted)} of {n(holdout.treatedPeople)} vs {n(holdout.holdoutConverted)} of {n(holdout.holdoutPeople)} held out</small>
                </>
              ) : (
                <>
                  <b className="text-[1rem] font-semibold text-warn">Too early to call</b>
                  <small className="text-[11.5px] text-muted">{holdout.reason ?? 'Not enough people yet.'}</small>
                </>
              )}
            </div>
          </div>
          <div className="flex items-start gap-2.5 rounded-[10px] border border-brand-b/42 bg-brand-a/10 px-3 py-2.5 text-[12.5px]">
            <span className="text-[15px] leading-tight text-brand-b" aria-hidden="true">◆</span>
            <div className="flex flex-1 flex-col gap-0.5">
              <b className="font-semibold text-white">Sense is watching</b>
              <span className="text-muted">
                {statsError ? statsError : stats ? `Counts as of ${fmtIst(stats.generatedAt)} IST · refreshed every 30 s.` : 'Loading live counts…'}
              </span>
            </div>
          </div>
        </div>
      )}
      {mode === 'edit' && live && isDraftOfLive && (
        <div role="status" className="border-b border-warn/28 bg-warn/7 px-3.5 py-2 text-[13px] text-muted" data-role="flow-draft-banner">
          Version {live.version} is live. You’re editing <b className="text-white">Version {editingVersion}</b> — people already inside finish on Version {live.version}.
        </div>
      )}

      {/* ---------------- canvas ---------------- */}
      <div className="relative min-h-0 flex-1">
        <div className="absolute inset-0 overflow-auto sm:hidden">
          <FlowList
            graph={shownGraph}
            data={data}
            mode={mode}
            triggerSummary={triggerSummary}
            selected={selected}
            menuKey={menu ? `${menu.nodeId}:${menu.portId}` : null}
            stats={statsByNode}
            onSelect={onSelect}
            onAdd={openMenu}
            drawer={!wide && drawer ? <div className="[&>aside]:relative [&>aside]:inset-auto [&>aside]:w-full [&>aside]:rounded-xl [&>aside]:border">{drawer}</div> : null}
            menu={wide ? null : addMenu(true)}
          />
          {/* In Test the panel is a bottom sheet over this list (max 62% of it):
              room underneath, so the last steps can still scroll above it. */}
          {mode === 'test' && <div aria-hidden="true" data-role="flow-test-spacer" className="h-[64%]" />}
        </div>
        <div
          ref={viewportRef}
          tabIndex={0}
          role="region"
          aria-label="Flow canvas — scroll, drag or use the arrow keys to pan; + and − zoom"
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onKeyDown={onViewportKey}
          className="absolute inset-0 hidden cursor-grab touch-pan-x touch-pan-y overflow-auto bg-surface-1 focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-brand-b active:cursor-grabbing sm:block"
        >
          <div data-sizer="" className={cn('relative', mode === 'test' && 'md:ml-[360px]')} style={{ width: canvasW * scale, height: H * scale }}>
            <div
              ref={canvasRef}
              data-role="flow-canvas"
              className="bg-dot-grid absolute left-0 top-0 origin-top-left"
              style={{ width: canvasW, height: H, transform: `scale(${scale})` }}
            >
              <Edges
                edges={shownGraph.edges}
                geo={geo}
                width={canvasW}
                height={H}
                onPath={testState?.pathEdges ?? null}
                volumes={volumes}
                suggested={running ? EMPTY_SET : suggestedIds}
                menuLine={menuAt ? `M ${menuAt.x + 10} ${menuAt.y} L ${menuAt.x + 30} ${menuAt.y}` : null}
              />
              {shownGraph.nodes.map((node) => (
                <NodeView
                  key={node.id}
                  node={node}
                  mode={mode}
                  data={data}
                  settings={shownSettings}
                  connected={connected.get(node.id) ?? EMPTY_SET}
                  selected={selected === node.id}
                  menuPort={menu && menu.nodeId === node.id ? menu.portId : null}
                  test={testMark(node.id)}
                  stat={statsByNode?.get(node.id) ?? null}
                  flagged={problem?.nodeId === node.id}
                  onSelect={onSelect}
                  onAdd={openMenu}
                  onAccept={onAccept}
                  onReject={onReject}
                  onDragStart={mode === 'edit' ? onDragStart : undefined}
                />
              ))}
            </div>
          </div>
          {menuScreen && <div className="hidden sm:block">{addMenu(false)}</div>}
        </div>

        <div role="group" aria-label="Zoom" className={cn('absolute bottom-3 left-3 z-[15] hidden items-center gap-0.5 rounded-[10px] border border-line-strong bg-[rgba(10,8,24,.88)] p-[3px] sm:flex', mode === 'test' && 'md:left-[366px]')}>
          <button type="button" aria-label="Zoom out" className="grid size-7 cursor-pointer place-items-center rounded-[7px] text-muted hover:bg-white/[0.08] hover:text-white focus-visible:outline-2 focus-visible:outline-brand-b" onClick={() => zoom(-0.1)}>
            <IcMinus size={14} />
          </button>
          <span aria-live="polite" className="w-[42px] text-center font-mono text-[11px] text-muted">{Math.round(scale * 100)}%</span>
          <button type="button" aria-label="Zoom in" className="grid size-7 cursor-pointer place-items-center rounded-[7px] text-muted hover:bg-white/[0.08] hover:text-white focus-visible:outline-2 focus-visible:outline-brand-b" onClick={() => zoom(0.1)}>
            <IcPlus size={14} />
          </button>
          <button type="button" aria-label="Fit the flow to the width" className="grid size-7 cursor-pointer place-items-center rounded-[7px] text-muted hover:bg-white/[0.08] hover:text-white focus-visible:outline-2 focus-visible:outline-brand-b" onClick={() => zoom('fit')}>
            <IcFit size={14} />
          </button>
        </div>
        {mode === 'edit' && !selNode && !menu && !toast && (
          <p className="pointer-events-none absolute bottom-3.5 right-3.5 z-[14] m-0 hidden whitespace-nowrap rounded-full border border-line bg-[rgba(10,8,24,.85)] px-3.5 py-1.5 text-[12px] text-muted lg:block">
            Click a step to configure it · <b className="text-white">+</b> on any free port adds the next step · drag a step to move it, the canvas to pan
          </p>
        )}

        {wide && drawer}

        {mode === 'test' && (
          <TestPanel
            people={testPeople?.people ?? null}
            source={testPeople?.source ?? null}
            peopleError={testPeopleError}
            personId={personId}
            setPersonId={setPersonId}
            walk={walk}
            walking={walking}
            walkError={walkError}
            idx={testState?.idx ?? 0}
            setIdx={setTestIdx}
            versionLabel={testVersion === 'draft' ? `the draft, Version ${editingVersion}` : `live Version ${live?.version ?? ''}`}
            onExit={() => switchMode('edit')}
          />
        )}

        {sheet && (
          <Preflight
            pf={pf}
            loading={pfLoading}
            error={pfError}
            version={editingVersion}
            relaunch={live !== null}
            paused={paused}
            launching={launching}
            suggestedIds={suggestedIds}
            onClose={closeSheet}
            onLaunch={() => void launch()}
            onRetry={() => void runPreflight()}
            onFix={fixCheck}
            onAccept={(id) => {
              onAccept(id);
              window.setTimeout(() => void runPreflight(), 60);
            }}
            onReject={(id) => {
              onReject(id);
              window.setTimeout(() => void runPreflight(), 60);
            }}
          />
        )}

        <div className="pointer-events-none absolute left-1/2 top-3 z-[60] flex w-[min(420px,calc(100%-24px))] -translate-x-1/2 justify-center" aria-live="polite">
          {toast && !sheet && (
            <div className="rounded-[10px] bg-white px-3 py-2 text-center text-[12.5px] font-medium leading-snug text-[#120E28] shadow-[0_18px_40px_-12px_rgba(0,0,0,.8)] motion-safe:animate-pop">{toast}</div>
          )}
        </div>
      </div>
    </div>
  );
}
